import React, { useRef, useState } from 'react'
import { isOverflown } from 'particles/utils'
import { GridRenderCellParams, GridStateColDef } from '@mui/x-data-grid-pro'
import styled from 'particles/themes/styled'
import Tooltip from 'particles/Tooltip'
import Icon, { IconName } from 'particles/Icon'
import Divider from 'components/Shared/Divider'
import LinkButton from 'components/Shared/Button/LinkButton'
import { PLATFORM_ENUM } from 'services/api/constants'
import { SocialMediaAccount } from 'state/submissions'
import { PLATFORM_REDIRECT_URL } from 'services/numbers'
import Text from 'particles/Text'
import { GridColDefProps } from 'components/Submissions/Review/Submissions/SimpleTable/constants'
import { useNewTranslation } from 'hooks'
import { TFunction } from 'i18next'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { abbreviateNumber } from 'components/utils'

export interface SocialMediaInfoCellProps extends GridRenderCellParams {
    onClick?: () => void
    column?: GridStateColDef
    hideUsername?: boolean
}

export interface SocialMediaAccountProps {
    username: string
    domain: string
    followerCount: number
}

export interface SocialMediaDataProps {
    profiles: SocialMediaAccount[]
}

interface SubFieldProps {
    field: string
    headerName: string
}

interface GridCellProps {
    dataProps: SocialMediaDataProps
    subFields: SubFieldProps[]
    onClick?: () => void
    hideUsername?: boolean
}

interface SocialMediaItemProps {
    followerCount: number | null
    username: string | null
    onClick?: () => void
    platformKey: PLATFORM_ENUM
    hideUsername?: boolean
    profileUrl?: string
}

export const PLATFORM_ICON_MAP: Partial<Record<PLATFORM_ENUM, IconName>> = {
    [PLATFORM_ENUM.IG]: 'instagramLogo',
    [PLATFORM_ENUM.FB]: 'facebookLogo',
    [PLATFORM_ENUM.TIKTOK]: 'tiktokLogo',
    [PLATFORM_ENUM.TWITTER]: 'twitterLogo',
    [PLATFORM_ENUM.XIAOHONGSHU]: 'xiaohongshuLogo',
}

export const platformFollowerLabels = (t: TFunction): Partial<Record<PLATFORM_ENUM, string>> => ({
    [PLATFORM_ENUM.IG]: t('submission.table.instagramFollowersLabel'),
    [PLATFORM_ENUM.FB]: t('submission.table.facebookFriendsLabel'),
    [PLATFORM_ENUM.TIKTOK]: t('submission.table.tikTokFollowersLabel'),
    [PLATFORM_ENUM.TWITTER]: t('submission.table.twitterFollowersLabel'),
    [PLATFORM_ENUM.XIAOHONGSHU]: t('submission.table.XHSFollowersLabel'),
})

export const FIELD_PLATFORM_MAP: Partial<Record<string, PLATFORM_ENUM>> = {
    [FIELDS_TYPE.IG_FOLLOWERS]: PLATFORM_ENUM.IG,
    [FIELDS_TYPE.IG_USERNAME]: PLATFORM_ENUM.IG,
    [FIELDS_TYPE.FB_FRIENDS]: PLATFORM_ENUM.FB,
    [FIELDS_TYPE.FB_USERNAME]: PLATFORM_ENUM.FB,
    [FIELDS_TYPE.TIKTOK_FOLLOWERS]: PLATFORM_ENUM.TIKTOK,
    [FIELDS_TYPE.TIKTOK_USERNAME]: PLATFORM_ENUM.TIKTOK,
    [FIELDS_TYPE.TWITTER_FOLLOWERS]: PLATFORM_ENUM.TWITTER,
    [FIELDS_TYPE.TWITTER_USERNAME]: PLATFORM_ENUM.TWITTER,
    [FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS]: PLATFORM_ENUM.XIAOHONGSHU,
    [FIELDS_TYPE.XIAOHONGSHU_USERNAME]: PLATFORM_ENUM.XIAOHONGSHU,
}

const SocialMediaItem = ({
    followerCount,
    username,
    platformKey,
    onClick,
    hideUsername,
    profileUrl,
}: SocialMediaItemProps) => {
    const [t] = useNewTranslation()
    const usernameRef = useRef<HTMLAnchorElement>(null)
    const [showTooltip, setShowTooltip] = useState(false)
    const [clicked, setClicked] = useState(false)

    const domain = PLATFORM_REDIRECT_URL[platformKey]
    const followerLabels = platformFollowerLabels(t)

    const handleMouseEnter = () => {
        const isUsernameOverflown = usernameRef.current ? isOverflown(usernameRef.current) : false
        setShowTooltip(isUsernameOverflown)
    }

    const handleLinkClick = () => {
        setClicked(true)
        onClick?.()
    }

    return (
        <StyledSocialMediaItem clicked={clicked} hideUsername={hideUsername}>
            <div className="icon-follower-section">
                <Icon name={PLATFORM_ICON_MAP[platformKey] as IconName} />
                <Tooltip
                    label={followerCount ? `${followerCount?.toLocaleString()} ${followerLabels[platformKey]}` : '-'}
                >
                    <Text scale="bodyS500" color={followerCount ? 'primary' : 'placeholder'}>
                        {followerCount && !hideUsername
                            ? abbreviateNumber(followerCount)
                            : followerCount
                              ? followerCount?.toLocaleString()
                              : '-'}
                    </Text>
                </Tooltip>
            </div>
            {!hideUsername && (
                <>
                    <Divider height="16px" width="1px" />
                    <Tooltip label={'@' + username} placement="top" open={showTooltip}>
                        <div style={{ overflow: 'hidden' }}>
                            <LinkButton
                                href={
                                    profileUrl ||
                                    (domain && username
                                        ? `${domain}${username.toString()}${platformKey === PLATFORM_ENUM.XIAOHONGSHU ? '' : '/'}`
                                        : undefined)
                                }
                                target="popup"
                                onClick={handleLinkClick}
                                onContextMenu={handleLinkClick}
                                value={username ? '@' + username : '-'}
                                ref={usernameRef}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={() => setShowTooltip(false)}
                                className="link"
                            />
                        </div>
                    </Tooltip>
                </>
            )}
        </StyledSocialMediaItem>
    )
}

const GridCellExpand = React.memo(function GridCellExpand({
    dataProps,
    subFields,
    onClick,
    hideUsername,
}: GridCellProps) {
    const { profiles } = dataProps

    const hasPlatform = (platform: PLATFORM_ENUM) =>
        subFields?.some(item => FIELD_PLATFORM_MAP[item.field] === platform)

    return (
        <StyledSocialMediaInfoContainer>
            <div className="cellValue">
                {profiles.map(
                    (profile: SocialMediaAccount) =>
                        hasPlatform(profile?.platform) && (
                            <SocialMediaItem
                                key={profile?.platform}
                                username={profile?.username}
                                followerCount={profile?.follower_count}
                                platformKey={profile?.platform}
                                onClick={onClick}
                                hideUsername={hideUsername}
                                profileUrl={profile?.profile_url}
                            />
                        ),
                )}
            </div>
        </StyledSocialMediaInfoContainer>
    )
})

export const SocialMediaInfoCell = (params: SocialMediaInfoCellProps) => {
    const dataProps: SocialMediaDataProps = params.value
    const { subFields } = (params?.colDef as GridColDefProps) ?? {}
    const { subFields: prioritySubFields } = (params?.column as GridColDefProps) ?? {} // This handles subfield data from Draggable Table component
    const onClick = params.onClick
    const hideUsername = params.hideUsername
    return (
        <GridCellExpand
            dataProps={dataProps}
            subFields={subFields || prioritySubFields || []}
            onClick={onClick}
            hideUsername={hideUsername}
        />
    )
}

const StyledSocialMediaInfoContainer = styled.div`
    align-items: center;
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;

    .cellValue {
        width: fit-content;
        min-width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
    }
`

interface StyledSocialMediaItemProp {
    clicked: boolean
    hideUsername?: boolean
}

const StyledSocialMediaItem = styled.div<StyledSocialMediaItemProp>`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[100]};

    .icon-follower-section {
        display: flex;
        align-items: center;
        gap: ${({ theme }) => theme.spacing[100]};
        min-width: 64px;
        width: ${({ hideUsername }) => (hideUsername ? '100%' : '64px')};
    }

    .link {
        overflow: hidden;
        display: block;
        white-space: nowrap;
        text-overflow: ellipsis;
        text-decoration: none;
        color: ${({ theme, clicked }) => (clicked ? theme.colors.text.actionActive : theme.colors.text.action)};
        ${({ theme }) => theme.textScale.bodyS500};
    }
`
